import classNames from 'classnames/bind';
import {
  multicityAddHandler,
  multicityDeleteHandler,
} from 'common/searchBox/tabContent/MultiCity';
import { DateFormat } from 'common/types';
import {
  Days,
  MAX_MULTI_CITY_COUNT,
  MIN_MULTI_CITY_COUNT,
  days,
} from 'common/utils';
import useHolidays from 'hooks/useHolidays';
import { MouseEvent, useCallback, useState } from 'react';
import { useInfiniteSeveral } from 'react-awesome-calendar-ts';
import DateInput from './dateInput';
import styles from './index.module.scss';
import RemoveSelectedDates from './removeSelectedDates';
import { ICalendar } from './types';

const cx = classNames.bind(styles);

const MultipleCalendar = ({
  searchParams,
  setDepartureDates,
  setSearchParams,
  closeModal,
  index,
}: ICalendar) => {
  const { itinerary } = searchParams;
  const Calendar = useInfiniteSeveral();

  const [{ editingIndex, selectedDates }, setOptions] = useState({
    selectedDates: itinerary
      ? itinerary.map(iti =>
          iti?.departureDate ? days(iti.departureDate, DateFormat.YMD) : null,
        )
      : [],
    editingIndex: index,
  });
  const [viewMonths, onViewMonthsUpdated] = useState<Days[]>([]);
  const holidays = useHolidays(viewMonths);

  const buttonTitles = itinerary.map((_, i) => `${i + 1}여정`);
  const clearSelectedDates = useCallback(() => {
    setDepartureDates([]);
    setOptions({
      selectedDates: [],
      editingIndex: 0,
    });
  }, []);

  const addHandler = multicityAddHandler(setSearchParams);
  const deleteHandler = multicityDeleteHandler(setSearchParams);

  const onAdd = useCallback(() => {
    addHandler();
    setOptions(prev => {
      const nextIndex = itinerary.findIndex((_, i) => !prev.selectedDates[i]);
      return {
        ...prev,
        editingIndex: nextIndex > -1 ? nextIndex : itinerary.length,
      };
    });
  }, [itinerary]);

  const onDelete = useCallback(
    (index: number) => {
      deleteHandler(index);
      setOptions(prev => {
        prev.selectedDates.splice(index, 1);
        const nextIndex = itinerary.findIndex((_, i) => !prev.selectedDates[i]);
        return {
          selectedDates: prev.selectedDates,
          editingIndex: Math.max(nextIndex, 0),
        };
      });
    },
    [itinerary],
  );

  const onSuccess = useCallback(
    (dates: Days[]) => {
      if (!dates?.length) {
        return;
      }
      const nextIndex = itinerary.findIndex((_, i) => !dates[i]);
      const scrollTargetElement = document.getElementById(
        `multipleDate${nextIndex >= 2 ? nextIndex - 2 : 0}`,
      );
      if (scrollTargetElement) {
        scrollTargetElement.scrollIntoView({
          block: 'nearest',
          inline: 'start',
        });
      }
      setOptions({
        selectedDates: dates,
        editingIndex: Math.max(nextIndex, 0),
      });
      setDepartureDates(dates);
      if (nextIndex < 0) {
        closeModal();
      }
    },
    [itinerary],
  );

  const onSelectDate = (idx: number) => (e: MouseEvent) => {
    setOptions(prev => ({ ...prev, editingIndex: idx }));
  };

  return (
    <div className={cx('calendar')}>
      <div className={cx('schedules')} data-triptype="MD">
        <div className={cx('inner')}>
          {itinerary.map((iti, i) => (
            <span id={`multipleDate${i}`} className={cx('date')}>
              <DateInput
                key={i}
                index={i}
                editing={editingIndex === i}
                dateInfo={selectedDates[i]}
                buttonTitles={buttonTitles}
                onSelect={onSelectDate(i)}
                cx={cx}
                clickCode={`cal.depdate${i + 1}`}
              />
              {i > MIN_MULTI_CITY_COUNT - 1 && (
                <button
                  type="button"
                  className={cx('removeway')}
                  title="일정 삭제"
                  data-event-nlog="click"
                  data-event-area-code={`cal.deldepdate${i + 1}`}
                  onClick={() => onDelete(i)}
                />
              )}
            </span>
          ))}
          {itinerary.length < MAX_MULTI_CITY_COUNT && (
            <button
              type="button"
              className={cx('addway')}
              title="일정 추가"
              data-event-nlog="click"
              data-event-area-code={`cal.adddepdate${itinerary.length + 1}`}
              onClick={onAdd}
            />
          )}
        </div>
      </div>
      <div
        className={cx('content')}
        data-event-nlog="click"
        data-event-area-code="cal.calendar"
      >
        <Calendar
          options={{
            dateFormat: DateFormat.YMD,
            dayTexts: ['일', '월', '화', '수', '목', '금', '토'],
            todayText: '오늘',
            selectionTextBuilder: ({ index, dates }) => {
              const dateMap = dates.reduce(
                (acc, date, dateIndex) =>
                  acc[date]
                    ? { ...acc, [date]: [...acc[date], dateIndex + 1] }
                    : { ...acc, [date]: [dateIndex + 1] },
                {},
              );
              const indexes = dateMap[dates[index]];
              return indexes.length > 1
                ? `${indexes[0]}${indexes.length > 2 ? '~' : ','}${
                    indexes[indexes.length - 1]
                  }여정`
                : `${indexes[0]}여정`;
            },
            maxSelection: MAX_MULTI_CITY_COUNT,
            maxMonths: 12,
            allowSame: true,
            height: '100%',
            visibleMonthCount: 14,
          }}
          mutableOptions={{
            selectedDates: selectedDates || [],
            editingIndex,
            holidays,
          }}
          methods={{
            onViewMonthsUpdated,
            onSuccess,
          }}
        />
      </div>
      <RemoveSelectedDates clearSelectedDates={clearSelectedDates} />
    </div>
  );
};

export default MultipleCalendar;
